import { useEffect, useMemo, useState } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { authedJSON } from '../api/http'
import { getPortfolio, getQuotesBatch } from '../api/trading'
import { getPortfolioSnapshots } from '../api/portfolioHistory'
import { useAuthStore } from '../auth/useAuthStore'
import { joinActiveTrades, type ActiveTradesView } from '../lib/activeTrades'
import { portfolioPollMs } from '../lib/marketHours'
import { aggregateDailyPL, localDateKey } from '../lib/pl'
import type { PortfolioResponse, Quote, QuotesResponse, Transaction } from '../api/types'
import { useTransactions } from './useTransactions'
import { useAppSymbols } from './useAppSymbols'
import { usePersistPositionSamples } from './usePersistPositionSamples'
import { useLiveQuote } from './useLiveQuote'
import { useLiveQuotesStore } from '../stores/useLiveQuotesStore'

async function fetchPortfolioForAccount(accountId: string): Promise<PortfolioResponse> {
  return authedJSON<PortfolioResponse>(`/api/accounts/${encodeURIComponent(accountId)}/portfolio`)
}

const QUOTE_TICK_MS = 1000
const QUOTE_BACKGROUND_MS = 5000

/**
 * Choose the portfolio equity to attribute a day's realized P/L against.
 * Prefer the latest snapshot strictly before midnight (prior-day close) so
 * the percent reflects "how much did trades move the account from where it
 * started." Fall back to the first snapshot of the day if no prior data
 * exists (e.g. snapshot recording started today). `points` must be sorted
 * by `t` ascending — which is how /api/portfolio/snapshots returns them.
 */
function pickStartOfDayEquity(
  points: { t: number; v: number }[],
  dayStartMs: number,
): number | undefined {
  let prevClose: number | undefined
  for (const p of points) {
    if (p.t < dayStartMs) {
      prevClose = p.v
      continue
    }
    return prevClose ?? p.v
  }
  return prevClose
}

function useTabVisible(): boolean {
  const [visible, setVisible] = useState(
    typeof document === 'undefined' ? true : document.visibilityState === 'visible',
  )
  useEffect(() => {
    function onChange() {
      setVisible(document.visibilityState === 'visible')
    }
    document.addEventListener('visibilitychange', onChange)
    return () => document.removeEventListener('visibilitychange', onChange)
  }, [])
  return visible
}

export interface DayStripItem {
  /** Local-time start of this day. */
  date: Date
  /** Closed positions (SELLs producing realized P/L via FIFO) on this day. */
  tradeCount: number
  /** Realized P/L on this day from FIFO close events. */
  realizedPL: number
  /**
   * realizedPL as a percentage of the portfolio equity at the start of this
   * day (prior-day close snapshot, or first snapshot of the day as fallback).
   * The "realized-trade contribution to account return" — excludes
   * mark-to-market on still-open positions. Undefined when no snapshot
   * covers the day (e.g. before snapshot recording started).
   */
  realizedPLPct?: number
}

export interface UseActiveTradesResult extends ActiveTradesView {
  isLoading: boolean
  /** True when the live quote query is currently using its slow / backoff cadence. */
  quoteThrottled: boolean
  /** True when the live quote query has had a recent failure (HTTP error). */
  quoteError: boolean
  /** Trigger an immediate refetch of all three data sources. */
  refreshAll: () => void
  /** Last 7 local days (today always included; empty prior days dropped). Oldest → newest. */
  dayStrip: DayStripItem[]
}

export interface UseActiveTradesOptions {
  /** Local-time day being viewed. Omit/null for today (live). */
  viewDate?: Date | null
}

export function useActiveTrades(options: UseActiveTradesOptions = {}): UseActiveTradesResult {
  const { viewDate } = options
  const accountId = useAuthStore((s) => s.accountId)
  const scopeAccountId = useAuthStore((s) => s.scopeAccountId)
  const accounts = useAuthStore((s) => s.accounts)
  const isAggregate = scopeAccountId === null && accounts.length > 1
  const visible = useTabVisible()
  const viewingToday =
    !viewDate ||
    (viewDate.getFullYear() === new Date().getFullYear() &&
      viewDate.getMonth() === new Date().getMonth() &&
      viewDate.getDate() === new Date().getDate())

  const {
    appSymbols,
    upstreamOrderIds,
    isLoading: appOrdersLoading,
    data: appOrdersData,
    refetch: refetchAppOrders,
  } = useAppSymbols()

  const aggregateAccountIds = useMemo(
    () => (isAggregate ? accounts.map((a) => a.accountId) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAggregate, accounts.map((a) => a.accountId).join(',')],
  )

  const portfolioQuery = useQuery({
    queryKey: ['portfolio', accountId],
    queryFn: getPortfolio,
    enabled: !!accountId && viewingToday && !isAggregate,
    refetchInterval: viewingToday ? portfolioPollMs : false,
  })

  const aggregatePortfolioQueries = useQueries({
    queries: aggregateAccountIds.map((id) => ({
      queryKey: ['portfolio', id],
      queryFn: () => fetchPortfolioForAccount(id),
      enabled: viewingToday,
      refetchInterval: viewingToday ? portfolioPollMs : false,
    })),
  })

  const mergedPortfolio = useMemo<PortfolioResponse | undefined>(() => {
    if (!isAggregate) return portfolioQuery.data
    if (aggregatePortfolioQueries.length === 0) return undefined
    if (aggregatePortfolioQueries.some((q) => !q.data)) return undefined
    const positions: PortfolioResponse['positions'] = []
    const orders: PortfolioResponse['orders'] = []
    for (let i = 0; i < aggregatePortfolioQueries.length; i++) {
      const accId = aggregateAccountIds[i]
      const data = aggregatePortfolioQueries[i].data
      if (data?.positions) {
        for (const p of data.positions) positions.push({ ...p, accountId: accId })
      }
      if (data?.orders) {
        for (const o of data.orders) orders.push({ ...o, accountId: accId })
      }
    }
    return { positions, orders } as PortfolioResponse
  }, [
    isAggregate,
    portfolioQuery.data,
    aggregateAccountIds,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    aggregatePortfolioQueries.map((q) => q.dataUpdatedAt).join(','),
  ])

  // Compute the active symbol set from the portfolio + app set without quotes yet,
  // so the quote query can fetch the right symbols. Quote refinement is re-applied
  // in the final memo below.
  const seedView = useMemo(
    () =>
      joinActiveTrades({
        upstreamOrderIds,
        appSymbols,
        portfolio: mergedPortfolio,
        quotesBySymbol: new Map(),
      }),
    [upstreamOrderIds, appSymbols, mergedPortfolio],
  )

  const liveSymbols = useMemo(() => {
    const list: string[] = []
    for (const p of seedView.positions) list.push(p.position.instrument.symbol)
    for (const o of seedView.pending) list.push(o.order.instrument.symbol)
    return [...new Set(list)].sort()
  }, [seedView])

  const symbolKey = liveSymbols.join(',')

  useLiveQuote(liveSymbols)
  const liveStatus = useLiveQuotesStore((s) => s.status)

  const quotesQuery = useQuery<QuotesResponse>({
    queryKey: ['active-quotes', symbolKey],
    queryFn: () => getQuotesBatch(liveSymbols),
    enabled: !!accountId && liveSymbols.length > 0 && viewingToday,
    refetchInterval: (query) => {
      if (!visible) return QUOTE_BACKGROUND_MS
      const fails = query.state.fetchFailureCount
      if (fails > 0) {
        // 1s → 2s → 4s → 8s (capped)
        const backoff = Math.min(8000, QUOTE_TICK_MS * 2 ** fails)
        return backoff
      }
      // Stream-active: poll slowly as a safety net. Stream offline or
      // unavailable: keep the existing 1s cadence so Public-only users
      // (or transient Schwab stream outages) see no regression.
      return liveStatus === 'online' ? 30_000 : QUOTE_TICK_MS
    },
    refetchIntervalInBackground: false,
    staleTime: 0,
    gcTime: 30_000,
  })

  const quotesBySymbol = useMemo(() => {
    const map = new Map<string, Quote>()
    for (const q of quotesQuery.data?.quotes ?? []) {
      map.set(q.instrument.symbol, q)
    }
    return map
  }, [quotesQuery.data])

  // Persist a 5s-bucketed sample per live symbol while polling is active, so
  // each closed position has price-shape data to render once it closes.
  usePersistPositionSamples(
    quotesBySymbol,
    !!accountId && viewingToday && liveSymbols.length > 0,
  )

  const txQuery = useTransactions()
  const transactionsBySymbol = useMemo(() => {
    const map = new Map<string, Transaction[]>()
    for (const t of txQuery.data ?? []) {
      if (!t.symbol) continue
      const arr = map.get(t.symbol) ?? []
      arr.push(t)
      map.set(t.symbol, arr)
    }
    return map
  }, [txQuery.data])

  const view = useMemo(
    () =>
      joinActiveTrades({
        upstreamOrderIds,
        appSymbols,
        portfolio: mergedPortfolio,
        quotesBySymbol,
        transactionsBySymbol,
        viewDate: viewDate ?? undefined,
      }),
    [
      upstreamOrderIds,
      appSymbols,
      mergedPortfolio,
      quotesBySymbol,
      transactionsBySymbol,
      viewDate,
    ],
  )

  const dailyPL = useMemo(
    () => aggregateDailyPL(txQuery.data ?? [], { symbolFilter: appSymbols }),
    [txQuery.data, appSymbols],
  )

  // Feeds the day-strip percentage badge with start-of-day portfolio equity.
  // 1W gives minute-level points for the whole 7-day window, which is exactly
  // what the strip spans. Refreshed minutely; cheap.
  const snapshotsQuery = useQuery({
    queryKey: ['portfolio-snapshots', accountId, '1W'],
    queryFn: () => getPortfolioSnapshots(accountId!, '1W'),
    enabled: !!accountId && !isAggregate,
    staleTime: 60_000,
  })

  const aggregateSnapshotQueries = useQueries({
    queries: aggregateAccountIds.map((id) => ({
      queryKey: ['portfolio-snapshots', id, '1W'],
      queryFn: () => getPortfolioSnapshots(id, '1W'),
      staleTime: 60_000,
    })),
  })

  // One snapshot series per account. Aggregating by timestamp here is wrong:
  // accounts sample at slightly different ts, so a "sum by t" leaves most
  // summed points containing only ONE account's equity, which then poisons
  // `pickStartOfDayEquity` (e.g. -$183 PL / one account's $200 equity = -91%).
  // The day-strip aggregates correctly by picking start-of-day per account
  // and summing those values.
  const snapshotPointsByAccount = useMemo<{ t: number; v: number }[][]>(() => {
    if (!isAggregate) {
      const pts = snapshotsQuery.data?.points
      return pts && pts.length > 0 ? [pts] : []
    }
    const series: { t: number; v: number }[][] = []
    for (const q of aggregateSnapshotQueries) {
      const pts = q.data?.points
      if (pts && pts.length > 0) series.push(pts)
    }
    return series
  }, [
    isAggregate,
    snapshotsQuery.data,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    aggregateSnapshotQueries.map((q) => q.dataUpdatedAt).join(','),
  ])

  const dayStrip = useMemo<DayStripItem[]>(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const seriesList = snapshotPointsByAccount
    const items: DayStripItem[] = []
    for (let i = 0; i < 7; i++) {
      const dayStart = new Date(today)
      dayStart.setDate(dayStart.getDate() - i)
      const key = localDateKey(dayStart.toISOString())
      const daily = dailyPL.get(key)
      const tradeCount = daily?.tradeCount ?? 0
      const realizedPL = daily?.pl ?? 0
      // Always surface today; only include prior days that had activity.
      if (i !== 0 && tradeCount === 0) continue
      // Sum start-of-day equity ACROSS accounts (each account picked
      // independently). Aggregating raw points by timestamp doesn't work —
      // see comment on snapshotPointsByAccount above.
      let startEquity: number | undefined
      let any = false
      for (const series of seriesList) {
        const e = pickStartOfDayEquity(series, dayStart.getTime())
        if (e != null) {
          startEquity = (startEquity ?? 0) + e
          any = true
        }
      }
      if (!any) startEquity = undefined
      const realizedPLPct =
        startEquity != null && startEquity > 0
          ? (realizedPL / startEquity) * 100
          : undefined
      items.push({ date: dayStart, tradeCount, realizedPL, realizedPLPct })
    }
    // Oldest → newest so today sits at the right edge of the strip.
    return items.reverse()
  }, [dailyPL, snapshotPointsByAccount])

  function refreshAll() {
    refetchAppOrders()
    if (isAggregate) {
      for (const q of aggregatePortfolioQueries) q.refetch()
    } else {
      portfolioQuery.refetch()
    }
    quotesQuery.refetch()
    txQuery.refetch()
  }

  const portfolioLoading = isAggregate
    ? aggregatePortfolioQueries.length === 0 || aggregatePortfolioQueries.some((q) => q.isLoading)
    : portfolioQuery.isLoading

  return {
    ...view,
    isLoading: (appOrdersLoading || portfolioLoading) && !appOrdersData,
    quoteThrottled: !visible || quotesQuery.failureCount > 0,
    quoteError: quotesQuery.isError,
    refreshAll,
    dayStrip,
  }
}

